import Script from 'next/script'
import { useEffect } from 'react'
import { initProductRating } from './lib/productRatingInitializer'
import { RATING_CLASS_NAME, RATING_PLACEHOLDER_CLASS_NAME } from './lib/constants'
import { useRouter } from 'next/router'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { DEFAULT_LOCALE } from '~/config/constants'
import { useTranslation } from 'next-i18next'

const ProductRatingScript: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE

  useEffect(() => {
    if (!document.querySelector('#rating-stars-styles')) {
      const linkElement = document.createElement('link')
      linkElement.setAttribute('rel', 'stylesheet')
      linkElement.setAttribute('type', 'text/css')
      linkElement.setAttribute('href', 'https://widget.reviews.io/rating-snippet/dist.css')
      linkElement.id = 'rating-stars-styles'
      document.head.appendChild(linkElement)
    }
  }, [])

  const onRatingSnippetLoad = () => {
    document.body.insertAdjacentHTML(
      'beforeend',
      `
      <style>
        .ruk_rating_snippet {
          --customStarIcon: url("/images/Icons/rating-star.svg");
          --customStarSize: 12px;
          --emptyStarColor: #CFCFCF;
        }

        @media (min-width: 1024px) {
          .ruk_rating_snippet {
            --customStarSize: 18px;
          }
        }

        .ruk_rating_snippet i {
          font-size: var(--customStarSize) !important;
          width: var(--customStarSize);
          height: var(--customStarSize);
          display: inline-block;
          vertical-align: middle;
          position: relative;
          color: transparent;
          background-image: none;
          -webkit-background-clip: initial;
          -moz-background-clip: initial;
          -webkit-text-fill-color: transparent;
          -moz-text-fill-color: transparent;
        }

        .ruk_rating_snippet i::after,
        .ruk_rating_snippet i::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center center;
        }

        .ruk_rating_snippet i::after {
          background-color: var(--emptyStarColor);
          -webkit-mask-image: var(--customStarIcon);
          mask-image: var(--customStarIcon);
          -webkit-mask-size: contain;
          mask-size: contain;
          -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
          -webkit-mask-position: center center;
          mask-position: center center;
          z-index: 2;
          mix-blend-mode: normal;
        }

        .ruk_rating_snippet i::before {
          background-image: var(--customStarIcon);
          z-index: 4;
        }

        .ruk-icon-percentage-star--100::before { clip-path: inset(0 0 0 0); }
        .ruk-icon-percentage-star--75::before { clip-path: inset(0 25% 0 0); }
        .ruk-icon-percentage-star--50::before { clip-path: inset(0 50% 0 0); }
        .ruk-icon-percentage-star--25::before { clip-path: inset(0 75% 0 0); }
        .ruk-icon-percentage-star--0::before { clip-path: inset(0 100% 0 0); }

        .ruk-rating-snippet-count {
          margin-left: 5px;
        }
      </style>
    `,
    )

    initProductRating(RATING_CLASS_NAME, finalLocale, {
      singular: t('review'),
      plural: t('reviews'),
    })

    const ratingPlaceholderNodes = document.querySelectorAll(`.${RATING_PLACEHOLDER_CLASS_NAME}`)

    ratingPlaceholderNodes.forEach((node) => {
      if (node && node.parentNode) {
        node.parentNode.removeChild(node)
      }
    })
  }

  return (
    <Script
      id="ruk-product-rating-script"
      src="https://widget.reviews.io/rating-snippet/dist.js"
      strategy="lazyOnload"
      onReady={onRatingSnippetLoad}
    />
  )
}

export default ProductRatingScript
